import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { LoadingSpinner } from '@/components/loading-spinner';
import { useNotification } from '@/components/notifications';
import { AuthContext } from '@/context/auth-context';
import { useGetNotificationPreferences } from '@/hooks/queries/use-get-notification-preferences';
import { useUpdateNotificationPreferences } from '@/hooks/mutations/use-update-notification-preferences';
import { useNotificationCenterStore } from '@/stores/notification-center-store';
import {
  NotificationEventType,
  NotificationPreference,
} from '@/types/notification-types';

const EVENT_TYPE_LABELS: Record<NotificationEventType, string> = {
  workflow_success: 'Succeeded',
  workflow_failure: 'Failed',
  trigger_files_detected: 'Picked up files',
  trigger_runs_started: 'Started a workflow',
  trigger_files_failed: "Couldn't process a file",
  access_requested: 'Access requested',
  workflow_shared: 'Workflow shared with you',
  agent_clarification: 'Agent needs clarification',
  human_intervention: 'Human intervention required',
};

const EVENT_TYPE_GROUPS: {
  title: string;
  eventTypes: NotificationEventType[];
}[] = [
  {
    title: 'Workflow runs',
    eventTypes: ['workflow_success', 'workflow_failure'],
  },
  {
    title: 'Triggers',
    eventTypes: [
      'trigger_files_detected',
      'trigger_runs_started',
      'trigger_files_failed',
    ],
  },
  {
    title: 'Sharing and access',
    eventTypes: ['access_requested', 'workflow_shared'],
  },
  {
    title: 'Needs your input',
    eventTypes: ['agent_clarification', 'human_intervention'],
  },
];

const EVENT_TYPE_ORDER: NotificationEventType[] = EVENT_TYPE_GROUPS.flatMap(
  (group) => group.eventTypes,
);

const CHANNEL_GRID = 'grid grid-cols-[1fr_60px_60px] items-center gap-1';

const HIT_AREA = 'relative before:absolute before:-inset-3 before:content-[""]';

type ChannelField = 'in_app_enabled' | 'email_enabled';

const buildDefaults = (): NotificationPreference[] =>
  EVENT_TYPE_ORDER.map((event_type) => ({
    event_type,
    in_app_enabled: true,
    email_enabled: false,
    slack_enabled: false,
  }));

export const NotificationPreferencesModal: FC = () => {
  const notification = useNotification();
  const { user } = useContext(AuthContext);
  const isOpen = useNotificationCenterStore((s) => s.isPreferencesOpen);
  const closePreferences = useNotificationCenterStore(
    (s) => s.closePreferences,
  );
  const { data, isLoading } = useGetNotificationPreferences();
  const updatePreferences = useUpdateNotificationPreferences();

  const [draft, setDraft] = useState<NotificationPreference[]>([]);

  const serverPreferences = useMemo(() => {
    const byType = new Map<NotificationEventType, NotificationPreference>();
    for (const pref of data?.items ?? []) {
      byType.set(pref.event_type, pref);
    }
    return buildDefaults().map((fallback) => ({
      ...fallback,
      ...byType.get(fallback.event_type),
      event_type: fallback.event_type,
    }));
  }, [data?.items]);

  useEffect(() => {
    setDraft(serverPreferences);
  }, [serverPreferences, isOpen]);

  const draftByType = useMemo(() => {
    const byType = new Map<NotificationEventType, NotificationPreference>();
    for (const pref of draft) {
      byType.set(pref.event_type, pref);
    }
    return byType;
  }, [draft]);

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(serverPreferences),
    [draft, serverPreferences],
  );

  const handleToggle = useCallback(
    (eventType: NotificationEventType, field: ChannelField, value: boolean) => {
      setDraft((prev) =>
        prev.map((pref) =>
          pref.event_type === eventType ? { ...pref, [field]: value } : pref,
        ),
      );
    },
    [],
  );

  const handleSave = useCallback(async () => {
    try {
      await updatePreferences.mutateAsync(draft);
      notification.success('Notification preferences saved');
      closePreferences();
    } catch {
      notification.error('Failed to save notification preferences');
    }
  }, [draft, notification, updatePreferences, closePreferences]);

  const description = user?.email
    ? `Choose how you want to be notified for each activity. Email is sent to ${user.email}.`
    : 'Choose how you want to be notified for each activity.';

  return (
    <Modal
      title='Notifications'
      description={description}
      isDialogOpen={isOpen}
      setIsDialogOpen={(open) => !open && closePreferences()}
      contentClassName='sm:max-w-[460px]'
      footerClassName='border-t pt-4'
      footer={
        <>
          <Button
            variant='outline'
            onClick={closePreferences}
            disabled={updatePreferences.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isDirty || updatePreferences.isPending}
          >
            {updatePreferences.isPending ? 'Saving...' : 'Save'}
          </Button>
        </>
      }
    >
      {isLoading ? (
        <div className='flex items-center justify-center py-12'>
          <LoadingSpinner />
        </div>
      ) : (
        <div className='flex flex-col'>
          <div className={`${CHANNEL_GRID} border-b px-2 pb-2`}>
            <span />
            <span className='text-muted-foreground text-center text-xs font-medium'>
              In-app
            </span>
            <span className='text-muted-foreground text-center text-xs font-medium'>
              Email
            </span>
          </div>

          {EVENT_TYPE_GROUPS.map((group) => (
            <div key={group.title}>
              <div className='text-muted-foreground px-2 pt-3.5 pb-1 text-[11px] font-semibold tracking-[0.06em] uppercase'>
                {group.title}
              </div>
              {group.eventTypes.map((eventType) => {
                const pref = draftByType.get(eventType);
                if (!pref) return null;
                const label = EVENT_TYPE_LABELS[eventType];
                return (
                  <div
                    key={eventType}
                    className={`${CHANNEL_GRID} hover:bg-muted/50 min-h-10 rounded-sm px-2`}
                  >
                    <span className='text-foreground text-sm leading-snug'>
                      {label}
                    </span>
                    <div className='flex justify-center'>
                      <Checkbox
                        className={HIT_AREA}
                        aria-label={`In-app notifications for ${group.title}: ${label}`}
                        checked={pref.in_app_enabled}
                        onCheckedChange={(value) =>
                          handleToggle(
                            eventType,
                            'in_app_enabled',
                            value === true,
                          )
                        }
                      />
                    </div>
                    <div className='flex justify-center'>
                      <Checkbox
                        className={HIT_AREA}
                        aria-label={`Email notifications for ${group.title}: ${label}`}
                        checked={pref.email_enabled}
                        onCheckedChange={(value) =>
                          handleToggle(
                            eventType,
                            'email_enabled',
                            value === true,
                          )
                        }
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};
